import { useCallback } from 'react';

import { useTrackCreditMeterUpsellClicked } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { type CreditUsageState, type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { getCreditUsageDisplayState, getPercentageUsed } from '~/components/CreditUsageMeter/utils';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useHasAnyAiAddons } from '~/swr-hooks/workspaces/addOns/useHasAnyAiAddons';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';
import { shouldUseFixedPlanUsageLayout } from '~/utils/shouldUseFixedPlanUsageLayout';

export type UseCreditUsageBreakdownParams = Pick<CreditUsageStats, 'allocated' | 'used'> & {
  giftCreditsBalance: number;
  allowCreditOverage: boolean;
};

export type UseCreditUsageBreakdownResult = {
  handleSeePlansClick: () => void;
  hasAiAddons: boolean;
  isCreditModel: boolean;
  percentageUsed: number;
  /** Fixed plans spend credits on AI actions only, so a category row would repeat the total. */
  showCategoryBreakdown: boolean;
  showPlansButton: boolean;
  usageState: CreditUsageState;
};

export const useCreditUsageBreakdown = ({
  allocated,
  allowCreditOverage,
  giftCreditsBalance,
  used,
}: UseCreditUsageBreakdownParams): UseCreditUsageBreakdownResult => {
  // Gift credits are part of the spendable total, so the usage state and percentage stay in sync with the count and bar.
  const totalAllocated = (allocated ?? 0) + giftCreditsBalance;

  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canUpgradePlan = canManageSubscription(permissions);
  const { data: subscription } = useSubscriptionContext();
  const usageState = getCreditUsageDisplayState({
    allowCreditOverage,
    used,
    totalCredits: totalAllocated,
  });
  const percentageUsed = Math.round(getPercentageUsed(used.total, totalAllocated));
  const hasAiAddons = useHasAnyAiAddons();
  const isCreditModel = !!subscription?.isCreditModel;

  const [showPlansModal] = useAirModal(PlansModal);
  const { trackCreditMeterUpsellClicked } = useTrackCreditMeterUpsellClicked();
  const creditAnalyticsParams = useCreditAnalyticsParams();

  const showPlansButton = canUpgradePlan && !subscription?.isOnEnterprisePlan && !!allocated;

  const handleSeePlansClick = useCallback(() => {
    if (creditAnalyticsParams) {
      trackCreditMeterUpsellClicked(creditAnalyticsParams);
    }
    showPlansModal({ leadFormTrigger: 'credit_meter' });
  }, [creditAnalyticsParams, showPlansModal, trackCreditMeterUpsellClicked]);

  return {
    handleSeePlansClick,
    hasAiAddons,
    isCreditModel,
    percentageUsed,
    showCategoryBreakdown: !shouldUseFixedPlanUsageLayout(subscription),
    showPlansButton,
    usageState,
  };
};
